import { Helmet } from 'react-helmet';
import { useEffect, useRef } from 'react';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('ab-visible'); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const stats = [
  { value: '17+',    label: 'Years of Excellence' },
  { value: '12,000+', label: 'Students Trained' },
  { value: '80%',    label: 'Placement Rate' },
  { value: '10+',    label: 'Courses Offered' },
];

const values = [
  { icon: '🎯', title: 'Practical Training',   desc: 'Hands-on learning with real accounting scenarios using TallyPrime.' },
  { icon: '🏆', title: 'Certified Excellence', desc: 'Awarded "Emerging Star" by Tally Education for outstanding quality.' },
  { icon: '💼', title: 'Job Placement',        desc: '80% of our graduates secure employment within months of completing.' },
  { icon: '📚', title: 'Expert Faculty',       desc: 'Led by Shrikant Singhai, a Master in TallyPrime with 17+ years experience.' },
  { icon: '🌐', title: 'Modern Curriculum',    desc: 'GST, TDS, TDL, Payroll — always updated with industry standards.' },
  { icon: '🤝', title: 'Affordable Access',    desc: 'Quality education made accessible and affordable for every student.' },
];

const About = () => {
  const storyRef  = useReveal();
  const statsRef  = useReveal();
  const valuesRef = useReveal();
  const ceoRef    = useReveal();
  const purposeRef = useReveal();

  return (
    <>
      <Helmet>
        <title>About Us | Shrikant Tally Institute – Damoh's Trusted Tally Training Center</title>
        <meta name="description" content="Learn about Shrikant Tally Institute, established in 2007 in Damoh, MP. Led by Shrikant Singhai, we have trained 12,000+ students with 80% placement rate. ISO 9001:2015 certified & Tally Education authorized partner." />
        <meta name="keywords" content="Shrikant Tally Institute about, Tally institute Damoh, Shrikant Singhai, Tally training center Damoh, accounting institute MP, ISO certified Tally institute, Tally Education partner Damoh, best accounting institute Damoh, Tally course Madhya Pradesh, Emerging Star Tally Education" />
        <meta property="og:title" content="About Shrikant Tally Institute | Damoh's #1 Tally Training Center" />
        <meta property="og:description" content="Established in 2007, Shrikant Tally Institute has trained 12,000+ students with 80% placement. ISO certified & Tally Education authorized partner in Damoh, MP." />
        <meta property="og:url" content="https://shrikanttallyinstitute.com/about" />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://shrikanttallyinstitute.com/about" />
      </Helmet>
      <style>{`
        .ab-reveal {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        .ab-reveal.ab-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Hero banner */
        .ab-hero {
          background: linear-gradient(135deg, #4d3d6c 0%, #6b4f9e 60%, #90b732 100%);
          padding: 72px 24px;
          color: #fff;
          position: relative;
          overflow: hidden;
        }
        .ab-hero::before {
          content: '';
          position: absolute;
          top: -60px; right: -60px;
          width: 320px; height: 320px;
          background: rgba(144,183,50,0.15);
          border-radius: 50%;
        }
        .ab-hero::after {
          content: '';
          position: absolute;
          bottom: -80px; left: -40px;
          width: 260px; height: 260px;
          background: rgba(255,255,255,0.06);
          border-radius: 50%;
        }
        .ab-hero h1 { font-size: clamp(2rem, 5vw, 3.2rem); font-weight: 800; }
        .ab-hero p  { font-size: 1.1rem; opacity: 0.88; max-width: 520px; }
        .ab-hero-badge {
          display: inline-block;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 50px;
          padding: 5px 16px;
          font-size: 13px;
          margin-bottom: 16px;
          backdrop-filter: blur(4px);
        }
        .ab-hero-img {
          max-height: 320px;
          filter: drop-shadow(0 12px 32px rgba(0,0,0,0.2));
          animation: abFloat 4s ease-in-out infinite;
        }
        @keyframes abFloat {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-12px); }
        }

        /* Stats */
        .ab-stats { background: #4d3d6c; padding: 48px 24px; }
        .ab-stat-item { text-align: center; color: #fff; }
        .ab-stat-item .num {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 800;
          color: #90b732;
          line-height: 1;
        }
        .ab-stat-item .lbl { font-size: 14px; opacity: 0.82; margin-top: 6px; }
        .ab-stat-divider {
          width: 1px; background: rgba(255,255,255,0.15);
          margin: 0 auto;
        }

        /* Story */
        .ab-story { padding: 72px 24px; background: #fff; }
        .ab-story-text { color: #555; line-height: 1.9; font-size: 15px; }
        .ab-story-text p { margin-bottom: 16px; }
        .ab-tag {
          display: inline-block;
          background: #ecf3d7;
          color: #4d7a10;
          border-radius: 50px;
          padding: 4px 14px;
          font-size: 12px;
          font-weight: 600;
          margin-right: 8px;
          margin-bottom: 8px;
        }

        /* Values */
        .ab-values { background: #f8fdf0; padding: 72px 24px; }
        .ab-value-card {
          background: #fff;
          border-radius: 14px;
          padding: 28px 24px;
          height: 100%;
          border: 1.5px solid #e8f5c8;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s;
        }
        .ab-value-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 32px rgba(144,183,50,0.15);
          border-color: #90b732;
        }
        .ab-value-icon {
          width: 52px; height: 52px;
          background: #ecf3d7;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 24px;
          margin-bottom: 14px;
        }
        .ab-value-card h6 { font-weight: 700; color: #2d2d2d; margin-bottom: 8px; }
        .ab-value-card p  { font-size: 13.5px; color: #777; margin: 0; line-height: 1.7; }

        /* Purpose */
        .ab-purpose {
          background: linear-gradient(135deg, #ecf3d7 0%, #fffbe5 100%);
          padding: 64px 24px;
          text-align: center;
        }
        .ab-purpose h2 { font-weight: 800; color: #4d3d6c; }
        .ab-purpose p  { font-size: 1.1rem; color: #555; max-width: 640px; margin: 0 auto; }
        .ab-purpose-quote {
          font-size: 1.25rem;
          font-weight: 700;
          color: #90b732;
          font-style: italic;
          margin: 20px 0;
        }

        /* CEO */
        .ab-ceo { background: #fff; padding: 72px 24px; }
        .ab-ceo-img-wrap {
          position: relative;
          display: inline-block;
        }
        .ab-ceo-img-wrap img {
          border-radius: 20px;
          box-shadow: 0 12px 40px rgba(77,61,108,0.15);
          max-width: 100%;
          transition: transform 0.4s ease;
        }
        .ab-ceo-img-wrap img:hover { transform: scale(1.02); }
        .ab-ceo-img-wrap .ab-award-badge {
          position: absolute;
          bottom: -16px; right: -16px;
          background: #90b732;
          color: #fff;
          border-radius: 12px;
          padding: 10px 16px;
          font-size: 12px;
          font-weight: 700;
          box-shadow: 0 4px 16px rgba(144,183,50,0.4);
          line-height: 1.4;
          text-align: center;
        }
        .ab-ceo-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #90b732;
          margin-bottom: 8px;
        }
        .ab-ceo h3 { font-weight: 800; color: #4d3d6c; }
        .ab-ceo-msg { color: #666; line-height: 1.85; font-size: 15px; }
        .ab-ceo-name { font-size: 16px; font-weight: 700; color: #4d3d6c; margin-top: 8px; }
        .ab-ceo-role { font-size: 13px; color: #90b732; }

        @media (max-width: 767px) {
          .ab-stat-divider { display: none; }
          .ab-ceo-img-wrap .ab-award-badge { right: 0; bottom: -12px; }
        }
      `}</style>

      {/* ── Hero ── */}
      <div className="ab-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0" style={{ animation: 'abFadeLeft 0.7s ease both' }}>
              <style>{`@keyframes abFadeLeft { from{opacity:0;transform:translateX(-30px)} to{opacity:1;transform:translateX(0)} }`}</style>
              <span className="ab-hero-badge">🏅 ISO 9001:2015 Certified Institute</span>
              <h1>About Shrikant<br />Tally Institute</h1>
              <p className="mt-3">Damoh's most trusted name in accounting education since 2007 — empowering youth with practical skills and guaranteed placement.</p>
              <div className="mt-4 d-flex flex-wrap gap-2">
                {['Est. 2007', 'Tally Authorized Partner', 'Emerging Star Award'].map(t => (
                  <span key={t} style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 50, padding: '4px 14px', fontSize: 12 }}>{t}</span>
                ))}
              </div>
            </div>
            <div className="col-md-6 text-center" style={{ animation: 'abFadeRight 0.7s ease both' }}>
              <style>{`@keyframes abFadeRight { from{opacity:0;transform:translateX(30px)} to{opacity:1;transform:translateX(0)} }`}</style>
              <img
                src="https://tallyeducation.com/tepl/wp-content/themes/TEPL/assets/images/aboutbanner.svg"
                alt="About Us"
                className="ab-hero-img img-fluid"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div ref={statsRef} className="ab-stats ab-reveal">
        <div className="container">
          <div className="row justify-content-center g-4">
            {stats.map((s, i) => (
              <div key={i} className="col-6 col-md-3">
                <div className="ab-stat-item">
                  <div className="num">{s.value}</div>
                  <div className="lbl">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Our Story ── */}
      <div ref={storyRef} className="ab-story ab-reveal">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-md-5 text-center">
              <img
                src={`${process.env.PUBLIC_URL}/dp.png`}
                alt="Institute"
                style={{ borderRadius: 20, maxWidth: '100%', boxShadow: '0 12px 40px rgba(77,61,108,0.12)' }}
              />
            </div>
            <div className="col-md-7">
              <p className="ab-ceo-label">Our Journey</p>
              <h2 className="fw-bold mb-3" style={{ color: '#4d3d6c' }}>Our Story</h2>
              <div className="ab-story-text">
                <p>Shrikant Tally Institute was established in <strong>2007</strong> near the SBI ATM in Damoh's Purana Thana area. Founded by <strong>Shrikant Singhai</strong>, a Master in TallyPrime, the institute was built with one goal — to prepare local youth for real-world jobs.</p>
                <p>Inspired by his own challenges, Shrikant Singhai aimed not only to teach but to empower young minds with practical knowledge and confidence to build successful careers.</p>
                <p>The institute offers courses including Tally Essential, Tally Professional, GST using TallyPrime, TDS, and TDL-Essential — with a strong emphasis on hands-on training. To date, over <strong>12,000 students</strong> have been trained, with about <strong>80% securing good jobs</strong>.</p>
                <p>The institute has been honored with the prestigious <strong>"Emerging Star from Tally Education"</strong> award, reflecting its commitment to quality education.</p>
              </div>
              <div className="mt-3">
                {['Tally Professional','GST Simulation','TDS & TDL','Office Automation','Payroll Management'].map(t => (
                  <span key={t} className="ab-tag">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Values ── */}
      <div ref={valuesRef} className="ab-values ab-reveal">
        <div className="container">
          <div className="text-center mb-5">
            <p className="ab-ceo-label" style={{ justifyContent: 'center', display: 'flex' }}>What Sets Us Apart</p>
            <h2 className="fw-bold" style={{ color: '#4d3d6c' }}>Why Choose Us</h2>
          </div>
          <div className="row g-4">
            {values.map((v, i) => (
              <div key={i} className="col-sm-6 col-lg-4">
                <div className="ab-value-card">
                  <div className="ab-value-icon">{v.icon}</div>
                  <h6>{v.title}</h6>
                  <p>{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Purpose ── */}
      <div ref={purposeRef} className="ab-purpose ab-reveal">
        <div className="container">
          <p className="ab-ceo-label" style={{ justifyContent: 'center', display: 'flex' }}>Our Mission</p>
          <h2>Our Purpose</h2>
          <div className="ab-purpose-quote mt-3">
            "Continuous Learning is the Only Way to Stay Relevant!"
          </div>
          <p>To positively impact the lives of learners by increasing employability, building practical skills, and creating pathways to financial independence through quality accounting education.</p>
        </div>
      </div>

      {/* ── CEO Message ── */}
      <div ref={ceoRef} className="ab-ceo ab-reveal">
        <div className="container">
          <div className="row align-items-center g-5 flex-column-reverse flex-md-row">
            <div className="col-md-7">
              <p className="ab-ceo-label">Leadership</p>
              <h3 className="mb-3">Message from Our Head</h3>
              <p className="ab-ceo-msg">
                At Shrikant Tally Institute, our purpose is to empower local youth by providing practical, high-quality education in accounting and business skills. We strive to equip every student with the knowledge and confidence to succeed in their careers, become job-ready, and contribute meaningfully to the economy.
              </p>
              <p className="ab-ceo-msg mt-3">
                Inspired by real-life challenges, we aim to make professional learning accessible, affordable, and impactful for all. By continuously incorporating new technologies and courses, we are committed to delivering even better services in the future.
              </p>
              <div className="mt-4 pt-3" style={{ borderTop: '2px solid #ecf3d7' }}>
                <div className="ab-ceo-name">Shrikant Singhai</div>
                <div className="ab-ceo-role">Founder & Head · Master in TallyPrime</div>
              </div>
            </div>
            <div className="col-md-5 text-center">
              <div className="ab-ceo-img-wrap d-inline-block">
                <img src={`${process.env.PUBLIC_URL}/dp.png`} alt="Shrikant Singhai" style={{ maxWidth: 300 }} />
                <div className="ab-award-badge">🏆 Emerging Star<br />Tally Education</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
